import React from 'react';
import { Target, Eye, Award, Users, Zap, Shield, CheckCircle } from 'lucide-react';
import { team } from '../data/mock';
import { engineeringImages } from '../data/images';

const AboutPageIndustrial = () => {
  const values = [
    {
      icon: Shield,
      title: 'Quality First',
      description: 'We never compromise on the quality of our engineering solutions'
    },
    {
      icon: Zap,
      title: 'Innovation',
      description: 'Constantly exploring new technologies and methodologies'
    },
    {
      icon: Users,
      title: 'Client Focus',
      description: 'Your success is our success - we put clients at the center'
    }
  ];

  const certifications = [
    { icon: Award, title: 'ISO 9001:2015 Certified' },
    { icon: CheckCircle, title: 'Quality Management System' },
    { icon: Shield, title: 'Engineering Excellence' }
  ];

  return (
    <div className="about-page-industrial">
      {/* Hero Section */}
      <section style={{
        padding: '140px 0 80px',
        background: 'var(--bg-page)',
        textAlign: 'center'
      }}>
        <div className="container-main">
          <span className="section-label">About Carboniq</span>
          <h1 className="hero-industrial-title" style={{ marginBottom: '1rem' }}>
            Leader in Industrial<br />Market Since 2010
          </h1>
          <p className="hero-industrial-subtitle" style={{ margin: '0 auto' }}>
            Your trusted partner in mechanical design excellence, delivering innovative solutions
          </p>
        </div>
      </section>

      {/* Mission, Vision, Values with Images */}
      <section className="section-padding" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="container-main">
          <div className="why-choose-grid">
            <div className="why-choose-image">
              <img src={engineeringImages.fea} alt="Engineering Analysis" />
            </div>

            <div className="why-choose-content">
              <span className="section-label">Our Foundation</span>
              <h2>Built on Excellence, Driven by Innovation</h2>
              <p className="why-choose-description">
                Carboniq Technologies is a full-service mechanical design company with over 15 years 
                of experience delivering innovative solutions across automotive, heavy equipment, 
                and industrial sectors.
              </p>

              <div className="why-choose-features">
                <div className="feature-item-industrial">
                  <div className="feature-icon-industrial"><Target size={24} /></div>
                  <div>
                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.25rem', fontWeight: 700 }}>Our Mission</h4>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Deliver cost-effective solutions that bring products to market faster</p>
                  </div>
                </div>

                <div className="feature-item-industrial">
                  <div className="feature-icon-industrial"><Eye size={24} /></div>
                  <div>
                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.25rem', fontWeight: 700 }}>Our Vision</h4>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Be the preferred mechanical design partner worldwide</p>
                  </div>
                </div>

                <div className="feature-item-industrial">
                  <div className="feature-icon-industrial"><Award size={24} /></div>
                  <div>
                    <h4 style={{ color: 'var(--text-primary)', marginBottom: '0.25rem', fontWeight: 700 }}>What Sets Us Apart</h4>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Deep expertise across multiple CAD platforms and industries</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding" style={{ background: 'var(--bg-page)' }}>
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="section-label">Core Values</span>
            <h2 className="heading-1" style={{ color: 'var(--text-primary)' }}>
              The Principles That<br />Guide Everything We Do
            </h2>
          </div>

          <div className="process-grid">
            {values.map((value, idx) => {
              const IconComponent = value.icon;
              return (
                <div key={idx} className="process-card">
                  <div style={{
                    width: '70px',
                    height: '70px',
                    background: 'rgba(253, 183, 20, 0.15)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <IconComponent size={32} style={{ color: 'var(--primary)' }} />
                  </div>
                  <h3 className="process-title">{value.title}</h3>
                  <p className="process-description">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="container-main">
          <div className="why-choose-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="why-choose-content">
              <span className="section-label">Certifications</span>
              <h2>We Are Certified Company</h2>
              <p className="why-choose-description">
                Our commitment to quality is reflected in our certifications and industry recognition.
              </p>

              <div className="why-choose-features">
                {certifications.map((cert, idx) => {
                  const IconComponent = cert.icon;
                  return (
                    <div key={idx} className="feature-item-industrial">
                      <div className="feature-icon-industrial">
                        <IconComponent size={24} />
                      </div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                        {cert.title}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="why-choose-image">
              <img src={engineeringImages.newProductDev} alt="Quality Standards" />
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section-padding" style={{ background: 'var(--bg-page)' }}>
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="section-label">Our Team</span>
            <h2 className="heading-1" style={{ color: 'var(--text-primary)' }}>
              Meet the Experts<br />Driving Our Vision
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {team.map((member) => (
              <div key={member.id} style={{
                background: 'var(--bg-card)',
                padding: '2rem',
                textAlign: 'center',
                border: '1px solid var(--border-color)',
                transition: 'transform 0.3s ease'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem',
                  fontSize: '3rem',
                  fontWeight: 900,
                  color: 'var(--text-inverse)'
                }}>
                  {member.name.charAt(0)}
                </div>
                <h3 style={{ 
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: '0.5rem'
                }}>
                  {member.name}
                </h3>
                <p style={{
                  color: 'var(--primary)',
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}>
                  {member.role}
                </p>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  {member.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Image Section */}
      <section style={{ background: 'var(--bg-section-alt)' }}>
        <div className="container-main" style={{ padding: '4rem 2.5rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            <div style={{ position: 'relative', height: '300px', overflow: 'hidden', borderRadius: '8px' }}>
              <img src={engineeringImages.cadDesign} alt="CAD Design" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ position: 'relative', height: '300px', overflow: 'hidden', borderRadius: '8px' }}>
              <img src={engineeringImages.sustenance} alt="Engineering Services" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPageIndustrial;